/**
 * AK-938 — выпадашка «алфавит + поиск» для справочников «Науки» (препараты,
 * единицы, хозяйства, поля, культуры, схемы).
 *
 * Сквозное правило ФМС: справочник в выпадашке — всегда по алфавиту и с
 * поиском, без «часто используемых». Собрано из примитивов DS (Popover +
 * Input + Button), своего визуального языка не вводит. Если поиск серверный
 * (тысячи препаратов) — `onSearch` отдаёт строку наверх, список приходит
 * уже отфильтрованным, алфавит наводим здесь.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { matchesSearch } from '@/lib/reference/normalizeSearch';
import { cn } from '@/lib/utils';

export interface PickItem {
  value: string;
  label: string;
  hint?: string | null;
}

interface Props {
  value: string | null;
  items: PickItem[];
  onChange: (value: string, item: PickItem) => void;
  placeholder: string;
  /** Подпись выбранного, если его нет в текущем списке (серверный поиск). */
  selectedLabel?: string | null;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Серверный поиск: список уже отфильтрован снаружи. */
  onSearch?: (q: string) => void;
  invalid?: boolean;
  className?: string;
  'aria-label'?: string;
}

const collator = new Intl.Collator('ru', { sensitivity: 'base' });

export function sortAlphabet(items: PickItem[]): PickItem[] {
  return [...items].sort((a, b) => collator.compare(a.label, b.label));
}

export function PickList({
  value,
  items,
  onChange,
  placeholder,
  selectedLabel,
  searchPlaceholder = 'Поиск по названию',
  emptyText = 'Ничего не нашлось',
  disabled,
  loading,
  onSearch,
  invalid,
  className,
  'aria-label': ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    const sorted = sortAlphabet(items);
    if (onSearch || !q.trim()) return sorted;
    return sorted.filter((i) => matchesSearch(`${i.label} ${i.hint ?? ''}`, q));
  }, [items, q, onSearch]);

  const current = items.find((i) => i.value === value);
  const label = current?.label ?? (value ? selectedLabel : null) ?? null;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o && q) {
          setQ('');
          onSearch?.('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          className={cn(
            'w-full min-w-0 justify-between font-normal',
            invalid && 'border-destructive',
            className,
          )}
        >
          <span className={cn('truncate', !label && 'text-muted-foreground')}>
            {label ?? placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              onSearch?.(e.target.value);
            }}
            placeholder={searchPlaceholder}
            className="h-8 pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto pt-2" role="listbox">
          {loading ? (
            <div className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Ищем…
            </div>
          ) : shown.length === 0 ? (
            <div className="px-2 py-2 text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            shown.map((i) => (
              <button
                key={i.value}
                type="button"
                role="option"
                aria-selected={i.value === value}
                onClick={() => {
                  onChange(i.value, i);
                  setOpen(false);
                  setQ('');
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
              >
                <Check
                  className={cn('h-4 w-4 shrink-0', i.value === value ? 'opacity-100' : 'opacity-0')}
                />
                <span className="min-w-0 flex-1 truncate">{i.label}</span>
                {i.hint && (
                  <span className="shrink-0 truncate text-xs text-muted-foreground">{i.hint}</span>
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
